/**
 * 应用层 i18n 组装（apps/web、apps/admin 共用；依赖 bundled-user.ts，只供前端使用）：
 * 内置种子（按使用端合并全部命名空间）+ 运行时词条包同步 + 缺译上报 + 永不显示词条编号。
 */
// 只引用用户端种子：后台种子（bundled-admin.ts）由 apps/admin 作为 loader 传入，后台命名空间不进用户端构建产物（INTEGRATE D-INT-14）
import { userSeedLoader } from './bundled-user.js';
import type { FallbackItem, Lang } from './langs.js';
import {
  createMissReporter,
  createRemoteSource,
  startRemoteSync,
  type MissReporter,
  type RemoteSource,
  type RemoteSourceOptions,
  type RemoteSync,
} from './remote.js';
import { createI18n, type I18n, type MessageLoader } from './runtime.js';
import type { Surface } from './surface.js';

export interface AppI18nRemoteOptions extends RemoteSourceOptions {
  /** 兜底拉取间隔（content.propagation_max_min），默认 5 分钟 */
  intervalMs?: number;
  /** 缺译上报合批等待时间，默认 5 秒 */
  missFlushDelayMs?: number;
  /** 直接注入数据源（测试） */
  source?: RemoteSource;
  onError?: (error: unknown) => void;
}

export interface AppI18nOptions {
  lang: Lang;
  /** 用户端（apps/web）或后台（apps/admin） */
  surface: Surface;
  /** 用户端默认取 userSeedLoader；后台必须传入 adminSeedLoader（`@nexora/i18n/bundled-admin`） */
  loader?: MessageLoader;
  namespaces?: readonly string[];
  fallbackOrder?: readonly FallbackItem[];
  /** `{param:<key>}` 的当前值文本（来自用户端可见参数） */
  param?: (key: string) => string | null | undefined;
  /**
   * 运行时词条包：用户端默认启用（`/api/v1/i18n/:lang`，PRD 5.14 A45 ≤5 分钟生效）；传 false 关闭。
   * 后台默认不启用（本阶段没有后台词条包接口）。
   */
  remote?: AppI18nRemoteOptions | boolean;
}

export interface AppI18n {
  i18n: I18n;
  /** 运行时词条包同步；未启用或未启动时为 null */
  readonly sync: RemoteSync | null;
  /** 缺译上报；未启用时为 null */
  readonly misses: MissReporter | null;
  /** 开始同步运行时词条包（可重复调用：先停止上一次），返回停止函数 */
  start(): () => void;
}

function missingAdminLoader(): never {
  throw new Error('createAppI18n: the admin surface needs loader: adminSeedLoader (@nexora/i18n/bundled-admin)');
}

export function createAppI18n(options: AppI18nOptions): AppI18n {
  const remoteOption = options.remote ?? options.surface === 'user';
  const remote: AppI18nRemoteOptions | null = remoteOption === false ? null : remoteOption === true ? {} : remoteOption;
  const source = remote ? (remote.source ?? createRemoteSource(remote)) : null;
  const misses =
    source && options.surface === 'user'
      ? createMissReporter({
          send: (lang, keys) => source.reportMisses(lang, keys),
          ...(remote?.missFlushDelayMs !== undefined ? { flushDelayMs: remote.missFlushDelayMs } : {}),
        })
      : null;

  const i18n = createI18n({
    lang: options.lang,
    loader: options.loader ?? (options.surface === 'user' ? userSeedLoader : missingAdminLoader()),
    ...(options.namespaces ? { namespaces: options.namespaces } : {}),
    ...(options.fallbackOrder ? { fallbackOrder: options.fallbackOrder } : {}),
    ...(options.param ? { param: options.param } : {}),
    // 永不显示词条编号（PRD 5.14 A45、spec-core §1.9 I4）
    missing: 'empty',
    ...(misses ? { onMissing: (key: string, lang: Lang) => misses.report(key, lang) } : {}),
  });

  let sync: RemoteSync | null = null;

  return {
    i18n,
    get sync() {
      return sync;
    },
    misses,
    start() {
      sync?.stop();
      sync = source
        ? startRemoteSync(i18n, {
            source,
            ...(remote?.intervalMs !== undefined ? { intervalMs: remote.intervalMs } : {}),
            ...(remote?.onError ? { onError: remote.onError } : {}),
          })
        : null;
      const started = sync;
      return () => {
        started?.stop();
        if (sync === started) sync = null;
        void misses?.flush();
      };
    },
  };
}
